/**
 * AddWidgetModal - Modal to add a new widget to a window
 * Supports configuration for task-list widgets
 */

import React, { useState } from 'react';
import {
  X,
  ListTodo,
  Calendar,
  CalendarRange,
  Clock,
  GitBranch,
  CalendarDays,
  CalendarCheck,
  Target,
  Bot,
  Repeat,
  StickyNote,
  Timer,
  Activity,
  ChevronRight,
  Settings,
  Check,
} from 'lucide-react';
import { useWindows } from '../../contexts/WindowsContext';
import type { WidgetType, TaskListWidgetConfig, NotesWidgetConfig } from '../../types/widgets';
import { WIDGET_LABELS } from '../widgets/WidgetRenderer';
import { TaskListConfigModal } from '../widgets/TaskListConfigModal';
import { DEFAULT_PROMPT_SETS } from '../../data/prompts';

interface AddWidgetModalProps {
  windowId: string;
  onClose: () => void;
}

const WIDGET_OPTIONS: {
  type: WidgetType;
  icon: React.ElementType;
  description: string;
  configurable?: boolean;
}[] = [
  { type: 'task-list', icon: ListTodo, description: 'Filtered list of tasks', configurable: true },
  { type: 'calendar-bar', icon: CalendarRange, description: 'Compact week view' },
  { type: 'calendar-full', icon: Calendar, description: 'Full month calendar' },
  { type: 'day-schedule', icon: Clock, description: "Today's time-based schedule" },
  { type: 'timeline', icon: GitBranch, description: 'Chronological task timeline' },
  { type: 'weekly-review', icon: CalendarDays, description: 'Weekly goals and review' },
  { type: 'monthly-review', icon: CalendarCheck, description: 'Monthly goals and review' },
  { type: 'yearly-review', icon: Target, description: 'Yearly vision and goals' },
  { type: 'ai-chat', icon: Bot, description: 'Chat with AI assistant' },
  { type: 'habit-tracker', icon: Repeat, description: 'Daily habit check-off with streaks' },
  { type: 'goals', icon: Target, description: 'Track goals with linked habits & tasks' },
  { type: 'notes', icon: StickyNote, description: 'Pin a journal prompt', configurable: true },
  { type: 'pomodoro', icon: Timer, description: 'Focus timer with start/pause/stop' },
  { type: 'activity-feed', icon: Activity, description: 'Chronological day activity log' },
];

// Preset configurations for quick task list setup
const TASK_LIST_PRESETS: { name: string; config: TaskListWidgetConfig }[] = [
  {
    name: "Today's Tasks",
    config: {
      title: "Today's Tasks",
      dateFilter: 'today',
      showCompleted: false,
      displayFields: ['status', 'horizon', 'deadline'],
    },
  },
  {
    name: 'This Week',
    config: {
      title: 'This Week',
      dateFilter: 'this-week',
      showCompleted: false,
      displayFields: ['status', 'date', 'horizon'],
    },
  },
  {
    name: 'Overdue',
    config: {
      title: 'Overdue',
      onlyOverdue: true,
      showCompleted: false,
      displayFields: ['status', 'deadline', 'horizon'],
    },
  },
  {
    name: 'Now (Urgent)',
    config: {
      title: 'Now',
      horizonFilter: ['now'],
      showCompleted: false,
      displayFields: ['status', 'date', 'horizon'],
    },
  },
  {
    name: 'All Tasks',
    config: {
      title: 'All Tasks',
      dateFilter: 'all',
      showCompleted: true,
      displayFields: ['status', 'date', 'horizon'],
    },
  },
];

type Step = 'select' | 'configure-task-list' | 'task-list-presets' | 'notes-source' | 'notes-prompt';

const NOTES_SOURCE_OPTIONS: { value: NotesWidgetConfig['sourceType']; label: string }[] = [
  { value: 'weekly', label: 'Weekly Review' },
  { value: 'monthly', label: 'Monthly Review' },
  { value: 'daily_start', label: 'Start of Day' },
  { value: 'daily_end', label: 'End of Day' },
  { value: 'daily_general', label: 'Daily Journal' },
  { value: 'yearly', label: 'Yearly Review' },
];

function getPromptsForSource(sourceType: NotesWidgetConfig['sourceType']): string[] {
  switch (sourceType) {
    case 'weekly':
      return [...DEFAULT_PROMPT_SETS.weekly_retrospective, ...DEFAULT_PROMPT_SETS.weekly_forward];
    case 'monthly':
      return [...DEFAULT_PROMPT_SETS.monthly_retrospective, ...DEFAULT_PROMPT_SETS.monthly_forward];
    case 'daily_start':
      return DEFAULT_PROMPT_SETS.start_day;
    case 'daily_end':
      return DEFAULT_PROMPT_SETS.end_day;
    case 'daily_general':
      return DEFAULT_PROMPT_SETS.general;
    case 'yearly':
      return DEFAULT_PROMPT_SETS.yearly;
  }
}

export function AddWidgetModal({ windowId, onClose }: AddWidgetModalProps) {
  const { addWidget } = useWindows();
  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);
  const [step, setStep] = useState<Step>('select');
  const [taskListConfig, setTaskListConfig] = useState<TaskListWidgetConfig>({
    title: '',
    dateFilter: 'all',
    showCompleted: true,
    displayFields: ['status', 'date', 'horizon'],
  });
  const [notesSourceType, setNotesSourceType] = useState<NotesWidgetConfig['sourceType']>('weekly');
  const [customPrompt, setCustomPrompt] = useState('');

  const handleSelectType = (type: WidgetType) => {
    setSelectedType(type);
    if (type === 'task-list') {
      setStep('task-list-presets');
    } else if (type === 'notes') {
      setStep('notes-source');
    } else {
      // Non-configurable widgets are added directly
      addWidget(windowId, type, {});
      onClose();
    }
  };

  const handleAddWidget = (config?: TaskListWidgetConfig) => {
    if (selectedType) {
      addWidget(windowId, selectedType, config || {});
      onClose();
    }
  };

  const handlePresetSelect = (preset: TaskListWidgetConfig) => {
    setTaskListConfig(preset);
    handleAddWidget(preset);
  };

  // Select widget type step
  if (step === 'select') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Add Widget</h3>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Widget options */}
          <div className="p-4 max-h-96 overflow-auto">
            <div className="grid grid-cols-1 gap-2">
              {WIDGET_OPTIONS.map(({ type, icon: Icon, description, configurable }) => (
                <button
                  key={type}
                  onClick={() => handleSelectType(type)}
                  className="flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-left group"
                >
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-600 dark:group-hover:bg-blue-900 dark:group-hover:text-blue-400 transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 dark:text-gray-200">
                      {WIDGET_LABELS[type]}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
                  </div>
                  {configurable && (
                    <Settings className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Task list presets step
  if (step === 'task-list-presets') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStep('select')}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
              </button>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Choose Task List Type
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Presets */}
          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Choose a preset or create a custom task list
            </p>

            <div className="grid grid-cols-1 gap-2">
              {TASK_LIST_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => handlePresetSelect(preset.config)}
                  className="flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-left"
                >
                  <ListTodo className="w-5 h-5 text-blue-500" />
                  <span className="font-medium text-gray-800 dark:text-gray-200">{preset.name}</span>
                </button>
              ))}
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <button
                onClick={() => setStep('configure-task-list')}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Custom Configuration...</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Custom configuration step (uses TaskListConfigModal)
  if (step === 'configure-task-list') {
    return (
      <TaskListConfigModal
        config={taskListConfig}
        onSave={(config) => handleAddWidget(config)}
        onClose={onClose}
      />
    );
  }

  // Notes widget - Step 1: Choose source type
  if (step === 'notes-source') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStep('select')}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
              </button>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Choose Source
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Which review type should this widget pull from?
            </p>
            <div className="grid grid-cols-1 gap-2">
              {NOTES_SOURCE_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => {
                    setNotesSourceType(value);
                    setStep('notes-prompt');
                  }}
                  className="flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-left"
                >
                  <StickyNote className="w-5 h-5 text-amber-500" />
                  <span className="font-medium text-gray-800 dark:text-gray-200">{label}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Notes widget - Step 2: Choose prompt
  if (step === 'notes-prompt') {
    const prompts = getPromptsForSource(notesSourceType);
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStep('notes-source')}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
              </button>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Choose Prompt
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Pick a prompt to pin, or type your own
            </p>

            <div className="max-h-60 overflow-auto space-y-1">
              {prompts.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => {
                    addWidget(windowId, 'notes', {
                      pinnedPrompt: prompt,
                      sourceType: notesSourceType,
                    } as NotesWidgetConfig);
                    onClose();
                  }}
                  className="w-full flex items-start gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left text-sm text-gray-700 dark:text-gray-300"
                >
                  <Check className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                  {prompt}
                </button>
              ))}
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                Custom prompt
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Type a custom prompt..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customPrompt.trim()) {
                      addWidget(windowId, 'notes', {
                        pinnedPrompt: customPrompt.trim(),
                        sourceType: notesSourceType,
                      } as NotesWidgetConfig);
                      onClose();
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (customPrompt.trim()) {
                      addWidget(windowId, 'notes', {
                        pinnedPrompt: customPrompt.trim(),
                        sourceType: notesSourceType,
                      } as NotesWidgetConfig);
                      onClose();
                    }
                  }}
                  disabled={!customPrompt.trim()}
                  className="px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
